import React, { memo, useEffect, useState } from "react";
import { X, CirclePlay, Download } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "../../component/commonComponent";
import { downloadFileNew, formatTime } from "../../utils/helper";
import moment from "moment-timezone";
import { useSelector } from "react-redux";

const CandidateCSATOverview = memo(
  ({
    isScreenSideSectionOpen,
    setIsScreenSideSectionOpen,
    sideSectionScreenData,
    setSideSectionScreenData,
  }) => {
    // console.log("CandidateCSATOverview > sideSectionScreenData:", sideSectionScreenData);

    const dynamicLink = useSelector((state) => state?.Candidate?.dynamicLink);

    const top = 50;
    const contentStyle = {
      height: `calc(100vh - ${top}px)`,
      top: `${top}px`,
      overflowY: "auto",
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    };

    const truncate = (text, limit = 50) =>
      text && text.length > limit ? text.slice(0, limit) + "..." : text;

    const handleJDDownload = () => {
      downloadFileNew(
        `${sideSectionScreenData?.jDPrefix}${sideSectionScreenData?.position?.jobDescriptionFileName}`,
        sideSectionScreenData?.position?.jobDescription,
      );
    };

    const handleResumeDownload = () => {
      downloadFileNew(
        `${sideSectionScreenData?.resumePrefix}${sideSectionScreenData?.candidate?.resumeFileName}`,
        sideSectionScreenData?.candidate?.resume,
      );
    };

    return (
      <div className="position-overview" style={contentStyle}>
        {/* Header */}
        <div className="position-overview__header">
          <div className="position-overview__header-content">
            <h2 className="position-overview__title">Interview Overview</h2>
            <div className="position-overview__title-row">
              <div>
                <div class="position-overview__candidate-icon">
                  {sideSectionScreenData?.candidate?.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
              </div>
              <div>
                <div className="position-overview__title-row">
                  <h1
                    className="position-overview__interview-title"
                    title={sideSectionScreenData.candidate?.name}
                  >
                    {truncate(sideSectionScreenData.candidate?.name)}
                  </h1>
                  <div className="position-overview__code-badge">
                    {sideSectionScreenData.code}
                  </div>
                </div>
                <div className="position-overview__title-row">
                  <h1 className="position-overview__position-title-sub">
                    {sideSectionScreenData.position?.code} -{" "}
                    {sideSectionScreenData.position?.title}
                  </h1>
                </div>
                <div className="position-overview__title-row">
                  <h1 className="position-overview__position-title-sub-2">
                    {sideSectionScreenData.candidate?.email} •{" "}
                    {sideSectionScreenData.candidate?.phone}
                  </h1>
                </div>
              </div>
            </div>
          </div>
          <button
            className="position-overview__close-btn"
            onClick={() => {
              setIsScreenSideSectionOpen(!isScreenSideSectionOpen);
              setSideSectionScreenData(null);
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="position-overview__actions">
          <div className="position-overview__action-buttons">
            {sideSectionScreenData.position?.jobDescription && (
              <button
                className="position-overview__action-btn"
                onClick={handleJDDownload}
              >
                <Download size={16} />
                JD
              </button>
            )}
            {sideSectionScreenData.candidate?.resume && (
              <button
                className="position-overview__action-btn"
                onClick={handleResumeDownload}
              >
                <Download size={16} />
                Resume
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="position-overview__content-3">
          <div className="position-overview__section">
            <div className="position-overview__page-details">
              <h3 className="position-overview__section-title">
                Interview Details
              </h3>

              {sideSectionScreenData?.status && (
                <div className="position-overview__status-container">
                  <div
                    className={`position-overview__status-badge status-${sideSectionScreenData?.status
                      ?.toLowerCase()
                      .replace(" ", "")}`}
                  >
                    {sideSectionScreenData?.status}
                  </div>
                </div>
              )}
            </div>

            <div className="position-overview__details">
              <div className="position-overview__detail-row">
                {sideSectionScreenData?.interviewType && (
                  <div className="position-overview__detail-item">
                    <div className="position-overview__detail-label">
                      Interview Type:
                    </div>
                    <div className="position-overview__detail-value">
                      {sideSectionScreenData?.interviewType?.join(", ")}
                    </div>
                  </div>
                )}

                <div className="position-overview__detail-item">
                  <div className="position-overview__detail-label">
                    Interview Date:
                  </div>
                  <div className="position-overview__detail-value">
                    {sideSectionScreenData?.interviewStartDate
                      ? formatDate(sideSectionScreenData?.interviewStartDate)
                      : "NA"}
                  </div>
                </div>
              </div>

              <div className="position-overview__detail-row">
                <div className="position-overview__detail-item">
                  <div className="position-overview__detail-label">
                    Duration:
                  </div>
                  <div className="position-overview__detail-value">
                    {sideSectionScreenData?.interviewDuration
                      ? formatTime(sideSectionScreenData?.interviewDuration)
                      : "NA"}
                  </div>
                </div>

                <div className="position-overview__detail-item">
                  <div className="position-overview__detail-label">
                    Interview Posted By:
                  </div>
                  <div className="position-overview__detail-value">
                    {sideSectionScreenData?.createdBy?.name}
                    <br />({sideSectionScreenData?.createdBy?.email})
                  </div>
                </div>
              </div>
              <hr className="position-overview__separate" />

              <div className="position-overview__detail">
                <div className="position-overview__detail-item">
                  <div className="position-overview__detail-label">
                    Screening Questions
                  </div>
                  <div className="position-overview__detail-value">
                    {sideSectionScreenData?.questions?.map((itm, index) => (
                      <div className="align-center w-100 position-relative">
                        <pre
                          className="question_item w-100 question-pre question-set-item flex-row align-items-start"
                          style={{ background: "transparent" }}
                        >
                          {index + 1}.{" "}
                          <code
                            className="w-100"
                            dangerouslySetInnerHTML={{
                              __html: itm.question,
                            }}
                          />
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {["Completed", "Disqualified", "Qualified"].includes(
                sideSectionScreenData?.status,
              ) && (
                <>
                  <hr className="position-overview__separate" />
                  <div className="position-overview__action-buttons">
                    {sideSectionScreenData?.position?.jobDescription && (
                      <a
                        href={`${sideSectionScreenData?.meetInviteDetails?.feedbackLink}?expires=${dynamicLink?.expires}&signature=${dynamicLink?.signature}`}
                        title="Feedback"
                        target="_blank"
                        rel="noreferrer"
                        className="position-overview__action-btn"
                      >
                        <Download size={16} />
                        Feedback Report
                      </a>
                    )}
                    {sideSectionScreenData?.meetInviteDetails
                      ?.recordingLink && (
                      <a
                        href={`${sideSectionScreenData?.meetInviteDetails?.recordingLink}?expires=${dynamicLink?.expires}&signature=${dynamicLink?.signature}`}
                        title="Recording"
                        target="_blank"
                        rel="noreferrer"
                        className="position-overview__action-btn"
                      >
                        <CirclePlay size={16} />
                        Interview Recording
                      </a>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {sideSectionScreenData?.interviewSurveyFeedback && (
          <div className="position-overview__content-2">
            <div className="position-overview__section">
              <div
                className="position-overview__page-details"
                style={{ alignItems: "stretch", height: "56px" }}
              >
                <h3
                  className="position-overview__section-title"
                  style={{ alignSelf: "center" }}
                >
                  Interview Survey Feedback
                </h3>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    alignSelf: "flex-end",
                    paddingBottom: "2px",
                  }}
                >
                  Submitted by: {sideSectionScreenData?.candidate?.name}
                </span>
              </div>
              <div style={{ padding: "4px 12px" }}>
                {[
                  {
                    q: "Q1. How was your overall experience?",
                    a: `${sideSectionScreenData?.interviewSurveyFeedback?.overallExperience?.score}/5`,
                  },
                  {
                    q: "Q2. Were the questions asked by the AI recruiter clear and easy to understand?",
                    a: `${sideSectionScreenData?.interviewSurveyFeedback?.questionClarity?.score}/5`,
                  },
                  {
                    q: "Q3. Was your experience with the AI interview better than, similar to, or worse than a human-led interview?",
                    a: sideSectionScreenData?.interviewSurveyFeedback
                      ?.aiVsHumanComparison?.value,
                  },
                  {
                    q: "Q4. Additional Comments (Optional)",
                    a: sideSectionScreenData?.interviewSurveyFeedback
                      ?.additionalComments,
                  },
                ].map((item, i, arr) => (
                  <div key={i}>
                    <p
                      style={{
                        margin: "8px 0 2px",
                        color: "#6b7280",
                        fontSize: "13px",
                        fontWeight: 400,
                      }}
                    >
                      {item.q}
                    </p>
                    <p
                      style={{
                        margin: "0 0 8px",
                        color: "#111827",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Ans. {item.a}
                    </p>
                    {i < arr.length - 1 && (
                      <hr className="position-overview__separate" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="position-overview__content-2">
          <div className="position-overview__section">
            <div className="position-overview__page-details">
              <h3 className="position-overview__section-title">
                Activity Logs
              </h3>
            </div>
            <table className="w-100 table-list f-l f-reg mb-2 mt-2">
              <thead>
                <tr className="tl-header f-sem-bol">
                  <td className="text-center">Time</td>
                  <td className="text-center">Action</td>
                  <td className="text-center">Updated By</td>
                </tr>
              </thead>
              <tbody>
                {sideSectionScreenData?.logs.map((list, index) => (
                  <tr className="tl-row rounded mb-2" key={index}>
                    <td className="text-center">
                      {moment(list?.createdAt).format("llll")}
                    </td>
                    <td className="text-center">{list?.action}</td>
                    <td className="text-center">{list?.user}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  },
);

export default CandidateCSATOverview;
